import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import type {
  ColDef,
  GetQuickFilterTextParams,
  GridReadyEvent,
  ITooltipParams,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
  ValueFormatterParams,
  ICellRendererParams,
} from "ag-grid-community";
import AudioPlayer from "react-h5-audio-player";
import CallMade from "@mui/icons-material/CallMade";
import CallReceived from "@mui/icons-material/CallReceived";
import { useRef, useState, useLayoutEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

//Styles
import "react-h5-audio-player/lib/styles.css";
import "../../assets/css/AudioPlayer.css";

// Local
import { getTimestampFormatted } from "../../assets/js/agGridUtils";
import HeadingOne from "../../components/HeadingOne";
import SearchBox from "../../components/SearchBox";
import { StyledTooltip } from "../../components/StyledTooltip";
import {
  ExistingStiltPhoneCall,
  getReadableCallStatus,
} from "../../models/calls";
import { CALLS } from "../../strings";
import { phoneUtils } from "../../utils";
import * as urls from "../../urls";
import voicemail from "../../images/voicemail.svg";
import { ButtonViewCustomer } from "../../components/ButtonViewCustomer";

interface Props {
  calls: ExistingStiltPhoneCall[];
  children: {
    dateRangePicker: React.ReactNode;
    voipSettingsBtn: React.ReactNode;
    // audioPlayer: React.ReactNode;
  };
}

// TODO: add a button where the user can nuke their local storage item `callBubblePosition`

const commonColProps = {
  flex: 1,
  resizable: true,
  filter: true,
  sortable: true,
  headerClass:
    "pl-6 pr-3 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase",
  cellStyle: {
    alignContent: "center",
    // // truncate cell text --
    // overflow: "hidden",
    // textOverflow: "ellipsis",
    // whiteSpace: "nowrap",
  },
};

const columnDefs: ColDef<ExistingStiltPhoneCall>[] = [
  {
    field: "direction",
    headerName: "",
    headerClass: "px-6",
    headerTooltip: "Direction",
    cellRenderer: renderDirection,
    getQuickFilterText: getDirectionStr,
    tooltipValueGetter: getDirectionStr,
    maxWidth: 50,
    cellStyle: { overflow: "hidden", textOverflow: "clip" },
  },
  {
    field: "customerSnapshot.name",
    headerName: "Customer",
    cellRenderer: renderCustomer,
    getQuickFilterText: (params) =>
      params.data?.customerSnapshot?.name ?? "unknown",
    width: 150,
  },
  {
    field: "callStatus",
    headerName: "Status",
    cellRenderer: (params: ICellRendererParams) =>
      getReadableCallStatus(params.data.callStatus ?? ""),
    maxWidth: 120,
  },
  {
    field: "timestampCreated",
    headerName: "Date",
    valueFormatter: getTimestampFormatted,
    width: 150,
  },
  {
    headerName: "From",
    valueFormatter: getPhoneFrom,
    getQuickFilterText: getQFTPhoneFrom,
    width: 60,
  },
  {
    headerName: "To",
    valueFormatter: getPhoneTo,
    getQuickFilterText: getQFTPhoneTo,
    width: 60,
  },
  {
    colId: "recordingURL",
    headerName: "Recording",
    cellRenderer: renderAudioRecording,
  },
  {
    colId: "actions",
    cellRenderer: renderIconCell,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
    sortable: false,
    maxWidth: 80,
    flex: 0,
    cellStyle: {
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "end",
    },
  },
];

export default function CallsListPage(props: Props): JSX.Element {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<ExistingStiltPhoneCall>>(null);
  const [gridReady, setGridReady] = useState(false);

  useLayoutEffect(() => {
    if (gridRef.current && gridReady) {
      gridRef.current.api.sizeColumnsToFit();
      gridRef.current.api.setGridOption("suppressScrollOnNewData", true);
    }
  }, [gridReady]);

  const handleGridReady = useCallback(
    () => (_event: GridReadyEvent) => {
      setGridReady(true);
    },
    [],
  );

  const onSearch = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.api.setGridOption(
        "quickFilterText",
        (document.getElementById("search-box") as HTMLInputElement).value,
      );
    }
  }, []);

  const searchBox = (
    <SearchBox
      onInput={onSearch}
      id="search-box"
      widthClasses="w-full xs:max-w-sm"
    />
  );

  // See [Auto Size Strategy](https://ag-grid.com/react-data-grid/column-sizing/#auto-size-columns-to-fit-grid)
  const autoSizeStrategy = useMemo<
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy
  >(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 100,
      columnLimits: [
        {
          colId: "ID",
          minWidth: 150,
        },
      ],
    };
  }, []);

  return (
    <>
      <div className="mb-6 flex flex-col justify-between gap-x-4 gap-y-2 sm:flex-row sm:items-center">
        <HeadingOne>{CALLS}</HeadingOne>
        <span className="self-end sm:self-center">
          {props.children.voipSettingsBtn}
        </span>
      </div>
      <div className="flex flex-col-reverse gap-6 md:flex-row md:items-end md:justify-between">
        {searchBox}
        <span className="self-end">{props.children.dateRangePicker}</span>
      </div>
      {/*{props.children.audioPlayer}*/}
      <div className={"ag-theme-alpine isolate h-[1000px] max-h-[67vh]"}>
        <AgGridReact<ExistingStiltPhoneCall>
          reactiveCustomComponents
          ref={gridRef}
          onGridReady={handleGridReady}
          autoSizeStrategy={autoSizeStrategy}
          defaultColDef={commonColProps}
          className="mt-5 shadow"
          getRowId={(data) => data.data.id}
          rowData={props.calls ?? []}
          animateRows={false}
          // pagination={true}
          rowSelection="single"
          rowHeight={50}
          cacheQuickFilter={true}
          columnDefs={columnDefs}
          /*
              onFirstDataRendered={(params) => {
                if (didComeFromBack) {
                  const currentPage = localStorage.getItem(
                    "callsListContainerCurrentPage",
                  );

                  const pageToNavigate = JSON.parse(
                    currentPage ??
                      JSON.stringify(params.api.paginationGetCurrentPage()),
                  );
                  params.api.paginationGoToPage(pageToNavigate);
                }
              }}
              onPaginationChanged={(params) => {
                if (params.newPage) {
                  const currentPage = params.api.paginationGetCurrentPage();
                  localStorage.setItem(
                    "callsListContainerCurrentPage",
                    JSON.stringify(currentPage),
                  );
                }
              }}
              */
          onCellClicked={(event) => {
            if (event.column?.getId() !== "recordingURL") {
              const { data } = event;
              if (data?.id) {
                navigate(`${urls.CALLS_LIST_URL}/${data.id}/edit`);
              }
            }
          }}
        />
      </div>
    </>
  );
}

/*
function callIdRenderer(
  params: CustomCellRendererProps<ExistingStiltPhoneCall>,
): JSX.Element {
  const id = params.data?.id;
  return (
    <Link
      to={`${urls.CALLS_LIST_URL}/${id}/edit`}
      className="text-blue-500 underline"
    >
      {params.value}
    </Link>
  );
}
*/

function renderCustomer(
  params: CustomCellRendererProps<ExistingStiltPhoneCall>,
): JSX.Element {
  const id = params.data?.customerID;
  const name = params.data?.customerSnapshot?.name ?? "Unknown";

  if (!id) return <>{name}</>;

  return (
    <div className="flex items-center gap-1">
      <StyledTooltip title="View Customer">
        <ButtonViewCustomer
          customerID={id}
          overrideIconStyles={{
            width: "24px",
            height: "24px",
            padding: "2px",
          }}
        />
      </StyledTooltip>
      {name}
    </div>
  );
}

/*
function renderCallStatus(
  params: CustomCellRendererProps<ExistingStiltPhoneCall>,
): JSX.Element {
  const callStatus = params.data?.callStatus;
  // // display green check if call status is completed
  // if (callStatus === "completed") {
  //   return <div className="flex flex-col items-center">
  //       <div className="h-6 w-6"><CallMadeOutlined/></div>
  //       <div className="text-xs">{callStatus}</div>
  //     </div>;
  // }
  //   if (callStatus === "no-answer") {
  //     return <div className="flex flex-col items-center">
  //       <div className="h-6 w-6"><CallMissed/></div>
  //       <div className="text-xs">{callStatus}</div>
  //     </div>
  // }
  return <div>{callStatus}</div>;
}
  */

function renderDirection(params: ICellRendererParams): JSX.Element {
  if (
    params.data.direction === "outbound-api" ||
    params.data.direction === "outbound"
  ) {
    return <CallMade />;
  }
  if (params.data.direction === "inbound") return <CallReceived />;

  return <></>;
}

function renderAudioRecording(params: ICellRendererParams): JSX.Element {
  const url = params.data.recordingURL;

  if (!url) return <></>;

  return (
    <div className="mx-0">
      <AudioPlayer
        style={{
          padding: "2px",
          background: "transparent",
          boxShadow: "none",
        }}
        src={url}
        showJumpControls={false}
        layout={"horizontal-reverse"}
        customVolumeControls={[]}
        customAdditionalControls={[]}
      />
    </div>
  );
}

function renderIconCell(params: ICellRendererParams): JSX.Element {
  if (params.data.voicemail === true) {
    return (
      <div className="flex items-center">
        <StyledTooltip title="Voicemail">
          <img src={voicemail} width={25} />
        </StyledTooltip>
      </div>
    );
  }
  return <></>;
}

function getDirectionStr(
  params: GetQuickFilterTextParams | ITooltipParams,
): string {
  if (
    params.data?.direction === "outbound-api" ||
    params.data?.direction === "outbound"
  ) {
    return "Outbound";
  }
  if (params.data?.direction === "inbound") {
    return "Inbound";
  }
  return "";
}

function getPhoneFrom(params: ValueFormatterParams): string {
  if (!params.data.from) return "--";
  return phoneUtils.display(params.data.from);
}

function getPhoneTo(params: ValueFormatterParams): string {
  if (!params.data.to) return "--";
  return phoneUtils.display(params.data.to);
}

function getQFTPhoneFrom(params: GetQuickFilterTextParams): string {
  if (!params.data.from) return "--";
  // formatted and not, so user can search without having to use parenthesis and dashes
  return phoneUtils.display(params.data.from) + params.data.from;
}

function getQFTPhoneTo(params: GetQuickFilterTextParams): string {
  if (!params.data.to) return "--";
  // formatted and not, so user can search without having to use parenthesis and dashes
  return phoneUtils.display(params.data.to) + params.data.to;
}
